import React, { useState, useEffect } from 'react';
import { Modal, Button, List, Icon, Input, Form, Image, Segment } from 'semantic-ui-react';
import QRCode from 'qrcode.react';
import { StyledModal } from './SharedStyles';
import * as api from '../services/api';

const DelegateRosterModal = ({ open, onClose }) => {
  const [delegates, setDelegates] = useState([]);
  const [inviteData, setInviteData] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const [copySuccess, setCopySuccess] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  useEffect(() => {
    if (open) {
      loadDelegates();
      setInviteData(null);
      setCopySuccess(false);
      setEditingId(null);
      setConfirmDeleteId(null);
    }
  }, [open]);

  const loadDelegates = async () => {
    try {
      const data = await api.getDelegates();
      setDelegates(data);
    } catch (err) {
      console.error('Error loading delegates:', err);
    }
  };

  const handleGenerateInvite = async () => {
    try {
      const data = await api.createDelegateInvite();
      setInviteData(data);
      setCopySuccess(false);
    } catch (err) {
      console.error('Error generating invite:', err);
    }
  };

  const handleCopy = () => {
    if (inviteData?.url) {
      navigator.clipboard.writeText(inviteData.url);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    }
  };

  const handleEditStart = (delegate) => {
    setEditingId(delegate.delegate_id);
    setEditData({ mobile: delegate.mobile || '', description: delegate.description || '' });
  };

  const handleEditSave = async () => {
    try {
      await api.updateDelegate(editingId, editData);
      setEditingId(null);
      setEditData({});
      loadDelegates();
    } catch (err) {
      console.error('Error updating delegate:', err);
    }
  };

  const handleRemove = async (delegateUserId) => {
    try {
      await api.deleteDelegate(delegateUserId);
      setConfirmDeleteId(null);
      loadDelegates();
    } catch (err) {
      console.error('Error removing delegate:', err);
    }
  };

  return (
    <StyledModal open={open} onClose={onClose}>
      <Modal.Header>Delegates</Modal.Header>
      <Modal.Content>
        {/* Invite section */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1em', marginBottom: '1.5em' }}>
          <div style={{ flex: 1 }}>
            <Button primary onClick={handleGenerateInvite}>
              <Icon name="linkify" /> Generate Invite Link
            </Button>
            {inviteData && (
              <div style={{ marginTop: '0.75em' }}>
                <Input
                  fluid
                  readOnly
                  value={inviteData.url}
                  action={{
                    icon: copySuccess ? 'check' : 'copy',
                    color: copySuccess ? 'green' : undefined,
                    onClick: handleCopy,
                  }}
                />
                <p style={{ color: '#999', fontSize: '0.85em', marginTop: '0.5em' }}>
                  Single-use link. Expires {new Date(inviteData.expiresAt).toLocaleString()}.
                </p>
              </div>
            )}
          </div>
          {inviteData && (
            <div style={{ flexShrink: 0 }}>
              <QRCode value={inviteData.url} size={128} />
            </div>
          )}
        </div>

        {/* Delegate list */}
        <List divided relaxed>
          {delegates.map(d => (
            <List.Item key={d.delegate_id}>
              {editingId === d.delegate_id ? (
                <List.Content>
                  <Segment>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75em', marginBottom: '0.75em' }}>
                      <Image src={d.delegate_picture} avatar />
                      <strong>{d.delegate_name}</strong>
                      <span style={{ color: '#666' }}>{d.delegate_email}</span>
                    </div>
                    <Form onSubmit={(e) => { e.preventDefault(); handleEditSave(); }}>
                      <Form.Input
                        label="Mobile"
                        placeholder="Phone number"
                        value={editData.mobile}
                        onChange={(e) => setEditData(prev => ({ ...prev, mobile: e.target.value }))}
                      />
                      <Form.TextArea
                        label="Description"
                        placeholder="Describe their role..."
                        value={editData.description}
                        onChange={(e) => setEditData(prev => ({ ...prev, description: e.target.value }))}
                      />
                      <Button size="small" positive onClick={handleEditSave}>Save</Button>
                      <Button size="small" onClick={() => setEditingId(null)}>Cancel</Button>
                    </Form>
                  </Segment>
                </List.Content>
              ) : (
                <List.Content style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75em', flex: 1, minWidth: 0 }}>
                    <Image src={d.delegate_picture} avatar />
                    <div style={{ minWidth: 0 }}>
                      <div><strong>{d.delegate_name}</strong></div>
                      <div style={{ color: '#666', fontSize: '0.9em' }}>{d.delegate_email}</div>
                      {d.mobile && <div style={{ color: '#666', fontSize: '0.85em' }}>{d.mobile}</div>}
                      {d.description && <div style={{ color: '#888', fontSize: '0.85em', marginTop: '0.25em' }}>{d.description}</div>}
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5em', flexShrink: 0 }}>
                    <Icon name="edit" link onClick={() => handleEditStart(d)} />
                    {confirmDeleteId === d.delegate_id ? (
                      <>
                        <Button size="mini" negative onClick={() => handleRemove(d.delegate_id)}>Confirm</Button>
                        <Button size="mini" onClick={() => setConfirmDeleteId(null)}>Cancel</Button>
                      </>
                    ) : (
                      <Icon name="trash" link color="red" onClick={() => setConfirmDeleteId(d.delegate_id)} />
                    )}
                  </div>
                </List.Content>
              )}
            </List.Item>
          ))}
          {delegates.length === 0 && (
            <List.Item>
              <List.Content>
                <em>No delegates yet. Generate an invite link and share it to add delegates.</em>
              </List.Content>
            </List.Item>
          )}
        </List>
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={onClose}>Close</Button>
      </Modal.Actions>
    </StyledModal>
  );
};

export default DelegateRosterModal;
